// Centralized upload constraints + validation for the self-tape pipeline.
// Used by UploadStage (and any future ingest surfaces) to keep rules consistent.

export const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB
export const MAX_BATCH_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
export const MAX_FILES = 20;
export const LONG_CLIP_WARN_SEC = 20 * 60; // 20 min
export const IOS_SAFARI_WARN_BYTES = 1024 * 1024 * 1024; // 1 GB

export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/x-m4v",
] as const;

export const ALLOWED_EXTENSIONS = ["mp4", "mov", "m4v"] as const;

export interface VideoMetadata {
  durationSec: number;
  width: number;
  height: number;
}

export interface ValidatedFile {
  file: File;
  metadata: VideoMetadata;
  warnings: string[];
}

export interface RejectedFile {
  name: string;
  reason: string;
}

function getExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

export function isAllowedFormat(file: File): boolean {
  const ext = getExtension(file.name);
  const extOk = (ALLOWED_EXTENSIONS as readonly string[]).includes(ext);
  const mime = (file.type || "").toLowerCase();
  // Some browsers (notably iOS Safari) return empty MIME for .mov — accept
  // when the extension is allowed. Otherwise require BOTH a valid extension
  // and a recognized MIME (or empty MIME with a valid extension).
  if (!extOk) return false;
  if (!mime) return true;
  return (ALLOWED_VIDEO_TYPES as readonly string[]).includes(mime);
}

export function formatBytesShort(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(0)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Probe a video file by loading its metadata in a hidden <video> element.
 * Resolves with metadata or rejects with a friendly Error message.
 */
export function probeVideoMetadata(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    (v as HTMLVideoElement & { playsInline?: boolean }).playsInline = true;
    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      v.onloadedmetadata = null;
      v.onerror = null;
      URL.revokeObjectURL(url);
    };

    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(
        new Error(
          "This video could not be processed. The file may be corrupted or incomplete.",
        ),
      );
    }, 15000);

    v.onloadedmetadata = () => {
      if (settled) return;
      const duration = v.duration;
      const width = v.videoWidth;
      const height = v.videoHeight;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      if (
        !isFinite(duration) ||
        duration <= 0 ||
        !width ||
        !height
      ) {
        reject(
          new Error(
            "This video could not be processed. The file may be corrupted or incomplete.",
          ),
        );
        return;
      }
      resolve({ durationSec: duration, width, height });
    };

    v.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      reject(
        new Error(
          "This video could not be processed. The file may be corrupted or incomplete.",
        ),
      );
    };

    v.src = url;
  });
}

export function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}
